import { useCallback } from 'react';
import { InvoiceModel } from '@frontend/api-invoices';
import { formatCentsToCurrency } from '@frontend/currency';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { calculateAppliedOrEstimatedSurcharge } from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import { FieldChangeEvent, SwitchField, Text, useFormField, useTooltip } from '@frontend/design-system';

export type PaymentSurchargeLineProps = {
  invoice: InvoiceModel;
  show: boolean;
  trackingId: string;
  onChange: (active: boolean) => Promise<void>;
};

const styles = {
  container: {
    display: 'flex',
    columnGap: theme.spacing(2),
    alignItems: 'center',
  },
  label: {
    fontSize: theme.font.size.medium,
    color: theme.colors.neutral50,
  },
  value: {
    fontWeight: theme.font.weight.bold,
  },
};
export const PaymentSurchargeLine = ({ invoice, show, trackingId, onChange }: PaymentSurchargeLineProps) => {
  const { t } = useTranslation('payments');

  const field = useFormField(
    {
      value: invoice.surchargingEnabled,
      type: 'switch',
    },
    [invoice.surchargingEnabled]
  );

  const { tooltipProps, triggerProps, Tooltip } = useTooltip();

  const onHandleChange = useCallback(
    (e?: FieldChangeEvent) => {
      field.onChange(e);

      if (e && 'value' in e) {
        onChange(e.value as boolean);
      }
    },
    [field.onChange, onChange]
  );

  if (!show) {
    return null;
  }

  /**
   * Percent value will return 300 for 3%, so divide by 100 to display
   */
  const surchargePercent = (invoice.configuredSurchargeBasisPoints ?? 0) / 100;

  const calculatedSurchargeAmount = calculateAppliedOrEstimatedSurcharge(invoice);

  return (
    <div data-test-id={trackingId} style={styles.container}>
      <div css={{ width: '40%', display: 'flex', alignItems: 'center', columnGap: theme.spacing(2) }}>
        <div>
          <SwitchField {...field} name='surchargeActive' label={t('Include Surcharge')} onChange={onHandleChange} />
        </div>
        <div>
          <div {...triggerProps}>
            <Icon name='info' size={16} color='default' />
          </div>
          <Tooltip {...tooltipProps}>
            <Trans t={t}>
              <Text color='white'>
                Make sure if your customer is using a CC they are aware of the surcharge. Will not apply to Debit or
                ACH.
              </Text>
            </Trans>
          </Tooltip>
        </div>
      </div>
      {invoice.surchargingEnabled && (
        <>
          <div css={{ width: '30%' }}>
            <label css={styles.label}>{t('Surcharge Percentage')}</label>
            <div data-test-id={`${trackingId}--surcharge-percentage`} css={styles.value}>
              {surchargePercent}%
            </div>
          </div>
          <div css={{ width: '30%' }}>
            <label css={styles.label}>{t('Surcharge Amount')}</label>
            <div data-test-id={`${trackingId}--surcharge-amount`} css={styles.value}>
              {formatCentsToCurrency(calculatedSurchargeAmount)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
